/*
 * Post-deploy chunk-load recovery.
 *
 * After a deploy the container is swapped, so an already-open tab still holds the
 * PREVIOUS build's hashed chunk URLs (e.g. /static/js/252.53615e2f.chunk.js).
 * Those 404 once the new build is live, so the next lazy `import()` throws a
 * ChunkLoadError and the app looks broken until a manual hard refresh. The
 * recovery is always the same: reload once to pick up the new index.html + the
 * current chunk hashes. This module centralizes detection + a loop-guarded reload
 * so the ErrorBoundary, the lazy() wrapper, and a global listener all behave
 * consistently.
 */

// True when an error is a failed dynamic import / missing build chunk (webpack
// ChunkLoadError, the native dynamic-import message, or the case where an old
// chunk URL now resolves to index.html and is parsed as the wrong MIME type).
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = typeof error.name === 'string' ? error.name : '';
  const message = typeof error.message === 'string' ? error.message : '';
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /is not a valid JavaScript MIME type/i.test(message)
  );
}

const GUARD_KEY = 'mm:chunk-reload-at';
const GUARD_WINDOW_MS = 15000;

// Reload at most once per GUARD_WINDOW_MS. If a chunk still fails right after we
// already reloaded, the build is genuinely broken (not just stale), so we stop
// reloading and let the caller surface the error instead of looping forever.
// Returns true if a reload was triggered, false if the guard suppressed it.
export function reloadForChunkError() {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(GUARD_KEY) || 0);
  } catch (e) {
    // sessionStorage can be unavailable (private mode / quota); fall through and
    // still attempt a single reload.
  }
  if (last && Date.now() - last < GUARD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch (e) {
    // ignore — worst case the guard is a no-op and the reload still recovers
  }
  window.location.reload();
  return true;
}

// Global safety net for chunk errors that escape the React render tree — a bare
// `import()` whose rejection isn't caught, for example. The in-tree ErrorBoundary
// and lazyWithRetry handle the render path; this catches the rest. Idempotent.
let installed = false;
export function installChunkErrorAutoReload() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event && event.reason)) reloadForChunkError();
  });
  window.addEventListener('error', (event) => {
    if (event && isChunkLoadError(event.error)) reloadForChunkError();
  });
}
